import { Badge, Button, Typography } from '@qodo/design-system';
import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { usePricingConfig } from 'hooks/billing/usePricingConfig';
import { useShowsReversedMigrationOffer } from 'hooks/billing/useShowsReversedMigrationOffer';
import { ChevronRight } from 'lucide-react';
import { ACCOUNT_TIERS, CONVERSION_COHORTS } from 'types/billing';
import {
  getMigrationDaysRemaining,
  isMigratedFreeUser,
  isMigratedPaidUser,
} from 'utils/billing/conversion';
import { spawn } from 'utils/translations';
import { ConversionPopupButton } from 'components/Billing/ConversionPopupButton';
import { getTrialDiscountPct } from '../utils';
import { BonusTier, TIER_MONTHS, isBonusTier } from './consts';
import type { ConversionPopupTriggerSource } from 'services/mixpanel/types';

const spawnAlert = spawn('billing.bonus-alert');

type TrialBonusAlertProps = {
  triggerSource?: ConversionPopupTriggerSource;
  /** When provided, the CTA button calls this instead of directly opening the
   *  ConversionPopup — use for callers that need side effects (track / dismiss /
   *  PATCH) before the popup opens. */
  onCtaClick?: () => void;
  /** Controls which paid-migration text variant to show. Defaults to `'banner'`. */
  variant?: 'banner' | 'popup';
  /** Disables the CTA (e.g. while the plan ladder is still loading, or a Move is
   *  in flight) so it can't fire a no-op / duplicate action. */
  disabled?: boolean;
  /** Shows a spinner on the CTA while an action is in flight (e.g. the paid Move,
   *  which runs a multi-step server-side sequence). Only applies to the
   *  `onCtaClick` variant. */
  loading?: boolean;
};

export const TrialBonusAlert = ({
  triggerSource,
  onCtaClick,
  variant = 'banner',
  disabled = false,
  loading = false,
}: TrialBonusAlertProps) => {
  const { data: accountInfo } = useAccountInfo();
  const { trialEndingThresholdDays } = usePricingConfig();
  const migrationPerk = accountInfo?.migrationPerk;
  const tier = accountInfo?.tier;
  const isActiveTrial = accountInfo?.trial?.active === true;
  const isMigrated = isMigratedFreeUser(accountInfo);
  const isMigratedPaid = isMigratedPaidUser(accountInfo);
  // Reversed (post-cancel) user still within the coupon window AND not yet bought —
  // show the same decaying-discount messaging so the offer carries through to
  // checkout, and drops the moment they buy (hook folds in the no-subscription check).
  const isReversedOffer = useShowsReversedMigrationOffer();
  const isEnding =
    (accountInfo?.trial?.daysRemaining ?? Infinity) <= trialEndingThresholdDays;
  const isMigrationLastDay =
    isMigratedPaid &&
    getMigrationDaysRemaining(accountInfo?.migrationEndDate) === 1;

  if (!isActiveTrial && !isMigratedPaid && !isReversedOffer) return <></>;

  // Shared CTA button: `onCtaClick` takes priority (caller handles popup open +
  // side effects); falls back to ConversionPopupButton when only triggerSource
  // is provided.
  const showCta = Boolean(onCtaClick || triggerSource);

  const ctaButton = (buttonKey: string) => {
    if (!showCta) return <></>;

    if (onCtaClick) {
      return (
        <Button
          size="sm"
          icon={ChevronRight}
          iconPosition="right"
          onClick={onCtaClick}
          disabled={disabled}
          loading={loading}
          className="shrink-0"
        >
          {spawnAlert(buttonKey)}
        </Button>
      );
    }

    return (
      <ConversionPopupButton
        size="sm"
        icon={ChevronRight}
        iconPosition="right"
        triggerSource={triggerSource!}
        className="shrink-0"
      >
        {spawnAlert(buttonKey)}
      </ConversionPopupButton>
    );
  };

  if (isMigrated) {
    if (!migrationPerk?.active) {
      if (isEnding && showCta) {
        return ctaButton('button-select');
      }

      return <></>;
    }

    const { durationMonths: months } = migrationPerk;
    const discount = getTrialDiscountPct(accountInfo);
    const buttonKey = isEnding ? 'button-select' : 'button-upgrade';

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3 rounded-md bg-fill-action-emphasis-hover px-4 py-2.5 border border-border-panel-emphasis">
          <div className="flex flex-1 flex-col">
            <Typography
              variant="h5"
              as="span"
              className="font-normal text-white"
            >
              {spawnAlert('text-free', { discount, months })}
            </Typography>
          </div>
          {ctaButton(buttonKey)}
        </div>
      </div>
    );
  }

  // Reversed users: the coupon still applies at their standard checkout, so show
  // the live discount messaging (no CTA — the checkout button owns the action, so
  // this stays a plain, non-duplicated offer line).
  if (isReversedOffer) {
    const reversedDiscount = getTrialDiscountPct(accountInfo);
    const reversedMonths = migrationPerk?.durationMonths;

    if (reversedDiscount === undefined || reversedMonths === undefined) {
      return <></>;
    }

    const reversedTextKey =
      variant === 'popup' ? 'text-paid-popup' : 'text-paid';

    return (
      <div className="flex items-center gap-3 rounded-md bg-fill-action-emphasis-hover px-4 py-2.5 border border-border-panel-emphasis">
        <Typography
          variant="h5"
          as="span"
          className="font-normal text-white flex-1"
        >
          {spawnAlert(reversedTextKey, {
            discount: reversedDiscount,
            months: reversedMonths,
          })}
        </Typography>
      </div>
    );
  }

  // Only promise the bonus to accounts with a real, active perk — a native
  // trial has no migration coupon at checkout (PLT-4502).
  if (!migrationPerk?.active) return <></>;

  if (!tier || !isBonusTier(tier)) return <></>;

  const discount = getTrialDiscountPct(accountInfo);
  const months = TIER_MONTHS[tier as BonusTier];
  const isPaid = tier === ACCOUNT_TIERS.paid;
  const paidTextKey =
    isMigratedPaid && variant === 'popup' ? 'text-paid-popup' : 'text-paid';
  const textKey = isPaid ? paidTextKey : 'text-free';
  const buttonKey = isPaid ? 'button-paid' : 'button-select';
  const isAnnual =
    isMigratedPaid &&
    accountInfo?.conversionCohort === CONVERSION_COHORTS.annual;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 rounded-md bg-fill-action-emphasis-hover px-4 py-2.5 border border-border-panel-emphasis">
        {!isMigratedPaid && (
          <Badge
            aria-label={spawnAlert('badge')}
            variant="emphasis"
            size="lg"
            className="shrink-0 bg-fill-tag-emphasis-idle"
          >
            {spawnAlert('badge')}
          </Badge>
        )}
        <Typography
          variant="h5"
          as="span"
          className="font-normal text-white flex-1"
        >
          {spawnAlert(textKey, { discount, months })}
        </Typography>
        {isPaid && variant !== 'popup' && (
          <Typography
            variant="s"
            as="span"
            className="text-action-on-emphasis-disabled shrink-0"
          >
            {spawnAlert(
              isMigrationLastDay ? 'subtitle-paid-ending' : 'subtitle-paid',
            )}
          </Typography>
        )}
        {ctaButton(buttonKey)}
      </div>
      {isAnnual && (
        <p className="text-xs text-text-general-neutral-subtle">
          {spawnAlert('refund-note')}
        </p>
      )}
    </div>
  );
};
